import { useEffect, useState } from "react";
import { adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminSettingsPage() {
    const { settings, refresh } = useSettings();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (settings) setForm({ ...settings }); }, [settings]);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await adminUpdateSettings(form);
            await refresh();
            toast.success("Settings saved.");
        } catch (e) {
            toast.error("Save failed");
        } finally { setSaving(false); }
    };

    const F = ({ k, label, placeholder, type = "text", helper }) => (
        <div>
            <Label className="text-white/80">{label}</Label>
            <Input type={type} value={form[k] || ""} onChange={(e) => update(k, e.target.value)} placeholder={placeholder} className="mt-1 bg-black/40 border-white/10" data-testid={`admin-settings-${k.replace(/_/g,'-')}`} />
            {helper && <div className="text-[11px] text-white/50 mt-1">{helper}</div>}
        </div>
    );

    const Toggle = ({ k, label, helper }) => (
        <div className="flex items-start gap-3">
            <Switch checked={!!form[k]} onCheckedChange={(v) => update(k, v)} className="mt-0.5" data-testid={`admin-settings-${k.replace(/_/g,'-')}`} />
            <div>
                <Label className="text-white/80 cursor-pointer">{label}</Label>
                {helper && <div className="text-[11px] text-white/50 mt-0.5">{helper}</div>}
            </div>
        </div>
    );

    return (
        <div data-testid="admin-settings-page">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.32em] text-orange-300/90">Admin</div>
                <h1 className="font-display text-3xl tracking-[0.05em] text-white">Site Settings</h1>
            </div>

            <div className="space-y-6">
                <Card className="surface-card border-0">
                    <CardContent className="p-6 space-y-6">
                        <div className="font-display text-lg tracking-[0.05em] text-white/90">Business Info</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <F k="business_name" label="Business name" />
                            <F k="tagline" label="Tagline" />
                            <F k="address" label="Address" />
                            <F k="phone" label="Phone" />
                            <F k="email" label="Email" type="email" />
                            <F k="hours" label="Hours" />
                        </div>
                        <div className="divider-line" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <F k="instagram_url" label="Instagram URL" />
                            <F k="facebook_url" label="Facebook URL" />
                            <F k="tiktok_url" label="TikTok URL" />
                            <F k="youtube_url" label="YouTube URL" />
                        </div>
                        <div className="divider-line" />
                        <div className="grid grid-cols-1 gap-3">
                            <F k="google_calendar_embed_url" label="Google Calendar embed URL" placeholder="https://calendar.google.com/calendar/embed?src=..." helper="Find this in your public Google Calendar's 'Embed code' section." />
                            <F k="ga4_measurement_id" label="Google Analytics (GA4) measurement ID" placeholder="G-XXXXXXXXXX" helper="Drop in once you have GA4 set up." />
                        </div>
                        <div className="flex justify-end">
                            <button onClick={save} disabled={saving} className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase" data-testid="admin-settings-save">{saving ? "Saving..." : "Save Settings"}</button>
                        </div>
                    </CardContent>
                </Card>

                {/* Ordering Schedule */}
                <Card className="surface-card border-0">
                    <CardContent className="p-6 space-y-5">
                        <div>
                            <div className="font-display text-lg tracking-[0.05em] text-white/90">Ordering Schedule</div>
                            <div className="text-[11px] text-white/50 mt-0.5">Controls when the online order page accepts orders.</div>
                        </div>

                        <Toggle
                            k="ordering_enabled"
                            label="Ordering enabled (master switch)"
                            helper="Turn off to close ordering immediately regardless of schedule."
                        />

                        <div className="divider-line" />

                        <Toggle
                            k="ordering_auto_schedule"
                            label="Auto open/close by schedule"
                            helper="When on, ordering follows the hours below. When off, ordering stays open as long as the master switch is on."
                        />

                        <div className="grid grid-cols-2 gap-4">
                            <F k="ordering_open_time" label="Opens at (ET)" type="time" helper="Default 11:00 AM Eastern" />
                            <F k="ordering_close_time" label="Closes at (ET)" type="time" helper="Default 6:00 PM Eastern" />
                        </div>

                        <div className="divider-line" />

                        <Toggle
                            k="ordering_event_override"
                            label="Stay open during events"
                            helper="If there's a public event on the calendar today, ordering stays open until the event ends (or the time below)."
                        />

                        <F
                            k="ordering_event_close_time"
                            label="Event override close time (ET)"
                            type="time"
                            helper="Used if the event has no end time set. Default 9:00 PM Eastern."
                        />

                        <div className="flex justify-end">
                            <button onClick={save} disabled={saving} className="ember-btn px-4 py-2 rounded-md text-sm font-display tracking-[0.12em] uppercase">{saving ? "Saving..." : "Save Ordering Settings"}</button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
